import { CommonModule } from '@angular/common';
import { Component, DestroyRef, effect, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { IonicModule } from '@ionic/angular';
import { FieldType, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FormlySelectModule } from '@ngx-formly/core/select';
import { AudioService } from 'src/app/services/audio/audio.service';

@Component({
  selector: 'noise-level-type',
  standalone: true,
  imports: [CommonModule, IonicModule, FormlySelectModule],
  template: `
    <label class="field-label" [attr.for]="'formly_' + field.id">
      {{ to.label || 'Noise level (dB)'}}
      <span *ngIf="to.required" class="required">*</span>
    </label>

    <ion-item lines="none" class="nl-field">
      <ion-label (click)="openOptionsModal()">
        <div class="nl-sub" *ngIf="valueLabel(); else emptyText">{{ valueLabel() }}</div>
        <ng-template #emptyText><span class="nl-placeholder">Tap to measure</span></ng-template>
      </ion-label>
      <ion-button fill="outline" size="small" (click)="openMeasureModal($event)">Check noise level</ion-button>
    </ion-item>

    <ion-modal [isOpen]="isMeasureOpen()" (didDismiss)="closeMeasureModal()" class="nl-measure-modal">
      <ng-template>
        <div class="nl-modal">
          <div class="nl-modal-header">
            <div class="nl-title">Check noise level</div>
            <div class="nl-subtitle">Recording will be stopped after 10 sec</div>
          </div>

          <div class="nl-meter">
            <div class="nl-reading">{{audioService?.currentDBValue}}<span class="unit"> dB</span></div>
            <div class="nl-minmax">
             <div>Min: {{ minDb | number:'1.0-0' }}</div>
              <div>Max: {{ maxDb | number:'1.0-0' }}</div>
              <div>Avg: {{ avgDb | number:'1.0-0' }}</div>
            </div>
          </div>

          <div class="nl-timer">
            <ion-chip [outline]="true">{{ timerLeft() | number:'2.0' }} sec</ion-chip>

            <ion-label (click)="resetCountdownTimer()"class="nl-tip"><ion-icon name="reload-outline"></ion-icon> Reset</ion-label>
          </div>

          <div class="nl-actions">
            <ion-button [disabled]="!sessionDone()" (click)="saveMeasure()" expand="block">Save</ion-button>
            <!-- <ion-button fill="outline" (click)="closeMeasureModal()" expand="block">Cancel</ion-button> -->
          </div>
        </div>
      </ng-template>
    </ion-modal>

    <ion-modal [isOpen]="isOptionsOpen()" (didDismiss)="closeOptionsModal()" class="nl-measure-modal">
      <ng-template>
        <div class="nl-modal">
          <div class="nl-modal-header">
            <div class="nl-title">Select noise level (dB)</div>
          </div>

          <ion-list lines="full" class="nl-options">
            <ion-item *ngFor="let opt of (to.options | formlySelectOptions: field | async)" button detail="false" (click)="selectOption(opt)">
              <ion-label>{{ opt?.label ?? opt?.value?.value ?? opt }}</ion-label>
              <ion-icon *ngIf="valueLabel() === (opt?.label ?? opt?.value?.value ?? opt)" slot="end" name="checkmark-circle-outline"></ion-icon>
            </ion-item>
          </ion-list>
        </div>
      </ng-template>
    </ion-modal>
  `,
  styles: [
    `
    :host { display:block; }
    .nl-options{    border: 1px solid rgba(214, 214, 214, 1); border-radius: 10px; ion-label{     padding: 10px 0px; }}
    .nl-field { --padding-start: 12px; --inner-padding-end: 8px; border: 0.9px solid #b0b0b0; border-radius: 2px; margin-top: 8px; }
    .nl-field { ion-button{     --border-color: rgba(29, 67, 112, 1);  color: rgba(29, 67, 112, 1);} }
    .nl-label { font-size: 16px; font-weight: 600; }
    .nl-sub { font-size: 14px; opacity: .8; }
    .nl-placeholder { font-size: 14px; opacity: .6; }
    .nl-modal { padding: 20px; }
    .nl-modal-header { text-align: left; margin-bottom: 12px; }
    .nl-title { color: black; font-size: 18px;font-family: Nunito;font-weight: 700;line-height: 24px;word-wrap: break-word; }
    .nl-subtitle { color: #686868;font-size: 13px;font-family: Nunito;font-weight: 600;line-height: 15.60px;word-wrap: break-word }
    .nl-meter { display: grid; gap: 10px; align-items: center; justify-items: center; padding: 16px; border: 1px dashed var(--ion-color-step-300); border-radius: 12px; margin: 10px 0 6px;     border: 1px solid #bfbfbf;padding: 25px 10px;}
    .nl-reading { font-size: 40px; line-height: 1; font-weight: 800; }
    .nl-reading .unit { font-size: 16px; font-weight: 600; margin-left: 4px; opacity: .8; }
    .nl-minmax { width: 100%; display: flex; justify-content: space-between; font-size: 12px; opacity: .8;     font-size: 13px;    font-family: Nunito;    font-weight: 400;    line-height: 15.60px;    word-wrap: break-word;}
    .nl-timer {     display: flex;    gap: 12px;    align-items: center;    justify-content: space-around;    margin: 20px 0; ion-chip{          width: 40%;    text-align: center;    align-items: center;    justify-content: center;    height: 35px;    } }
    .nl-actions { display:grid; gap:10px; margin-top: 8px; ion-button{    --background: rgba(29, 67, 112, 1);    color: white;--border-radius: 25px;} }
    .nl-tip { display: flex;gap: 8px;align-items: center;    font-size: 14px;    color: #1D4370;    font-weight: 700; }
    .required{color: #dc2626; margin-left: 2px;}
    `,
  ],
})
export class NoiseLevelTypeComponent extends FieldType {
  public minDb: number = Number.POSITIVE_INFINITY;
  public maxDb: number = 0;
  public avgDb: number = 0;
  private dbSamples: number[] = [];

  public audioService = inject(AudioService);
  dbMeterInterval: any;

  private _isMeasureOpen = signal(false);
  isMeasureOpen = () => this._isMeasureOpen();

  private _isOptionsOpen = signal(false);
  isOptionsOpen = () => this._isOptionsOpen();

  private _timerLeft = signal(10);
  timerLeft = () => this._timerLeft();

  private _sessionDone = signal(false);
  sessionDone = () => this._sessionDone();

  private _labelSig = signal<string>('');
  label = () => this._labelSig();

  private _tickHandle: any = null;

  ngOnInit() {
    this._labelSig.set((this.formControl?.value as any)?.value ?? '');
    App.addListener('appStateChange', (state) => {
      if (state.isActive) {
       this.audioService.stopCapture();
      } else {
        this.audioService.stopCapture();
      }
    });
  }
  async ngOnDestroy() {
    this.audioService.stopCapture();
    if (this.dbMeterInterval) { clearInterval(this.dbMeterInterval); this.dbMeterInterval = null; }
  }


  startDBMeter() {
    this.minDb = 0;
    this.maxDb = 0;
    this.avgDb = 0;
    if (Capacitor.isNativePlatform()) {
      this.dbMeterInterval = setInterval(() => {
        console.log('Current DB Value=', this.audioService.currentDBValue);
        const current = this.audioService.currentDBValue || 0;
        this.minDb = Math.min(this.minDb, current);
        this.maxDb = Math.max(this.maxDb, current);
        this.dbSamples.push(current);
        this.avgDb = this.dbSamples.reduce((a, b) => a + b, 0) / this.dbSamples.length;
        console.log('Min:', this.minDb, 'Max:', this.maxDb, 'Avg:', this.avgDb);
      }, 1000);
    } else {
      this.dbMeterInterval = setInterval(() => {
        console.log('Current DB Value=', this.audioService.currentDBValue);
        const current = this.audioService.currentDBValue || 0;
        this.minDb = Math.min(this.minDb, current);
        this.maxDb = Math.max(this.maxDb, current);
        this.dbSamples.push(current);
        this.avgDb = this.dbSamples.reduce((a, b) => a + b, 0) / this.dbSamples.length;
        console.log('Min:', this.minDb, 'Max:', this.maxDb, 'Avg:', this.avgDb);
      }, 1000);
    }
  }

  openMeasureModal(e?: Event) {
    e?.preventDefault();
    this.audioService.startAudio();
    this.resetCountdown();
    this._isMeasureOpen.set(true);
    this.startCountdown();
  }
  closeMeasureModal() {
    this._isMeasureOpen.set(false);
    if (this._tickHandle) { clearInterval(this._tickHandle); this._tickHandle = null; }
    this.audioService.stopCapture();
    if (this.dbMeterInterval) { clearInterval(this.dbMeterInterval); this.dbMeterInterval = null; }
  }
  openOptionsModal() { this._isOptionsOpen.set(true); }
  closeOptionsModal() { this._isOptionsOpen.set(false); }

  startCountdown() {
    this.startDBMeter();
    let left = 10;
    this._timerLeft.set(left);
    this._sessionDone.set(false);
    this._tickHandle = setInterval(() => {
      left -= 1;
      this._timerLeft.set(left);
      if (left <= 0) {
        this._sessionDone.set(true);
        this.audioService.stopCapture();
        if (this._tickHandle) { clearInterval(this._tickHandle); this._tickHandle = null; }
      }
    }, 1000);
  }

  resetCountdownTimer() {
    this.audioService.startAudio();
    this.resetCountdown();
    this._isMeasureOpen.set(true);
    this.startCountdown();
  }
  resetCountdown() {
    if (this._tickHandle) { clearInterval(this._tickHandle); this._tickHandle = null; }
    this._timerLeft.set(10);
    this._sessionDone.set(false);
  }
  saveMeasure() {
    this.closeMeasureModal();
    this.openOptionsModal();
  }

  selectOption(opt: any) {
    const lbl = opt?.label ?? '';
    const v = opt?.value ?? opt; 
    const key = (v && typeof v === 'object') ? (v.key ?? lbl) : lbl;
    const value = (v && typeof v === 'object') ? (v.value ?? lbl) : String(v);
    const recommendation = (v && typeof v === 'object' && v.recommendation) ? String(v.recommendation) : '';

    const stored: any = { key, value };
    if (recommendation) stored.recommendation = recommendation;
    stored.recordedAt = new Date().toISOString();

    this._labelSig.set(value);
    this.formControl.setValue(stored);
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
    this.closeOptionsModal();
  }

  valueLabel() {
    const v = this.formControl?.value as any;
    if (v && typeof v === 'object' && v.value) return String(v.value);
    return this.label() || '';
  }
}
